/**
 * Never a white screen. An error while showing a page is caught here, and the
 * menu stays usable.
 *
 * A page file that no longer exists ("Failed to fetch dynamically imported
 * module") means this tab still runs the previous release: its code asks for
 * files the new release replaced. Reloading once picks up the new version.
 * Anything else shows a message with Reload and Home.
 */
import { Component, type ReactNode } from 'react';
import { Button, EmptyState } from '../components';
import { useI18n } from '../lib/i18n';
import { useLocation } from '../lib/router';

const RELOAD_KEY = 'sss.staleVersionReload';
/** A second stale-file error within this time is a real failure, not an old tab: don't loop. */
const RELOAD_GUARD_MS = 30_000;

export function isStaleVersionError(e: unknown): boolean {
  const msg = e instanceof Error ? `${e.name} ${e.message}` : String(e);
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|ChunkLoadError/i.test(msg);
}

/** Reload to pick up the new release, at most once per RELOAD_GUARD_MS. Returns whether it reloads. */
export function reloadOnceForNewVersion(now = Date.now(), reload: () => void = () => window.location.reload()): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (now - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  reload();
  return true;
}

function PageCrash({ onRetry }: { onRetry: () => void }) {
  const { t } = useI18n();
  return (
    <EmptyState
      icon="alert"
      title={t('ui.common.pageErrorTitle')}
      description={t('ui.common.pageErrorBody')}
      action={
        <div className="flex flex-wrap justify-center gap-2">
          <Button icon="refresh" onClick={() => window.location.reload()}>
            {t('ui.common.reload')}
          </Button>
          <Button
            variant="secondary"
            icon="home"
            onClick={() => {
              onRetry();
              window.location.assign('/');
            }}
          >
            {t('ui.common.goHome')}
          </Button>
        </div>
      }
    />
  );
}

class Boundary extends Component<{ resetKey: unknown; children: ReactNode }, { error: unknown; key: unknown }> {
  override state = { error: null as unknown, key: this.props.resetKey };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  static getDerivedStateFromProps(props: { resetKey: unknown }, state: { error: unknown; key: unknown }) {
    // Moving to another page clears the error.
    return props.resetKey !== state.key ? { error: null, key: props.resetKey } : null;
  }

  override componentDidCatch(error: unknown) {
    if (isStaleVersionError(error)) reloadOnceForNewVersion();
    console.error('Page failed to render', error);
  }

  override render() {
    if (this.state.error === null) return this.props.children;
    return <PageCrash onRetry={() => this.setState({ error: null })} />;
  }
}

/** Wraps the routed page; resets when the address changes. */
export function PageErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <Boundary resetKey={pathname}>{children}</Boundary>;
}
